import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import AddressItem from '../../../../components/AddressItem';
import Button from '../../../../components/Button';
import FeeItem from '../../../../components/FeeItem/FeeItem';
import FeePaymentConfiguration from '../../../../components/FeePaymentConfiguration';
import FeePaymentDisclaimer from '../../../../components/FeePaymentDisclaimer';
import FeePaymentMethod from '../../../../components/FeePaymentMethod';
import Icon, { Icons } from '../../../../components/Icon/Icon';
import PageHeader from '../../../../components/PageHeader';
import StickyPanel from '../../../../components/StickyPanel';
import AppConfig from '../../../../config';
import { t } from '../../../../lang';
import HelperService from '../../../../services/HelperService';
import RequestService from '../../../../services/RequestService';
import TypesService from '../../../../services/TypesService';
import { sendSlice } from '../../../../store/slices';
import SendAmountInput from './components/SendAmountInput/SendAmountInput';

import Decimal from 'decimal.js';
import PaymentPurpose from '../../../../components/PaymentPurpose';
import { Margin } from '../../../../components/classNames';
import './SendAmount.scss';

const { routes } = AppConfig;

const BlockchainsById = TypesService.BlockchainsById;
const CurrenciesById = TypesService.CurrenciesById;

const BTC_ID = 1;
const TRX_ID = 2;
const USDT_TRC20_ID = 3;
const CREDIT_CURRENCY_ID = 8;
const LTC_ID = 10;

const commissionTypesByCurrencyId = {
	[BTC_ID]: 3,
	[TRX_ID]: 0,
	[CREDIT_CURRENCY_ID]: 1,
	[USDT_TRC20_ID]: 2,
	[LTC_ID]: 3,
};
const WARNIN_ICON_SIZE = 24;
const debouncedRequestTimeout = 1000;

const SendAmount = () => {
	const [loading, setLoading] = useState(false);

	const sendAmountBodyRef = useRef(null);

	const permissions = useSelector(state => state.app.permissions);

	const tariffData = useSelector(store => store.app.tariffData);
	const asset = useSelector(state => state.send.asset) || {};
	const account = useSelector(store => store.send.account) || {};
	const recipient = useSelector(state => state.send.recipient);
	const amount = useSelector(state => state.send.amount);
	const comment = useSelector(state => state.send.comment);
	const feeData = useSelector(store => store.send.feeData);
	const withdrawalId = useSelector(store => store.send.withdrawalId);
	const haveActiveTransactionWarning = useSelector(store => store.send.haveActiveTransactionWarning);
	const isMaxAmountSetted = useSelector(store => store.send.isMaxAmountSetted);
	const minAmountUsd = useSelector(store => store.send.minAmountUsd);
	const withdrawalCardId = useSelector(store => store.send.withdrawalCardId);
	const uploadFiles = useSelector(store => store.send.approvalFiles);

	const intl = useIntl();
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const billingFee = tariffData.maxTariff || 0;
	const feeByCurrencyIdForUSDT = {
		[USDT_TRC20_ID]: billingFee * 2,
		[CREDIT_CURRENCY_ID]: billingFee,
		[TRX_ID]: 18,
	};
	const currencyData = CurrenciesById[asset.Id];
	const currencyBlockchainData = BlockchainsById[currencyData.BlockchainId];

	const feeMethodSelecting = currencyData?.FeeType === 'method_selecting';
	const feeCalculateInBack = currencyData?.FeeType === 'back_calculating';
	const feeConfiguratingInFront = currencyData?.FeeType === 'front_configurating';

	const updateComment = e => {
		const comment = e.target.value;
		dispatch(sendSlice.actions.setComment(comment));
	};

	useEffect(() => {
		if (withdrawalId) {
			dispatch(sendSlice.actions.setWithdrawalId());
			RequestService.WithdrawalCancelPrepare([withdrawalId]);
		} else {
			dispatch(sendSlice.actions.setComment());

			if ((feeCalculateInBack && !currencyData.Standart) || feeConfiguratingInFront) {
				dispatch(
					sendSlice.actions.setFeeData({
						CurrencyId: asset.Id,
						BlockchainId: currencyData.BlockchainId,
						Amount: account.Amount,
						Fee: 0,
					})
				);
			}
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const calculateCommission = async (currencyId, sourceAddress, destinationAddress, amount, feeData) => {
		if (!amount) {
			dispatch(sendSlice.actions.setFeeData({ ...feeData, Loading: false }));
			return;
		}

		try {
			const response = await RequestService.CalculateCommission(
				[sourceAddress],
				destinationAddress,
				currencyId,
				amount
			);

			if (!response) throw Error;

			if (response.Status === 'Success') {
				const comission = response?.Commission || 0;
				const fee = feeData.FeeRedacted ? feeData.Fee : comission;

				dispatch(
					sendSlice.actions.setFeeData({
						...feeData,
						Fee: fee,
						DefaultFee: comission,
						Loading: false,
					})
				);
			} else {
				throw response;
			}
		} catch (error) {
			console.log(error);
			dispatch(
				sendSlice.actions.setFeeData({
					...feeData,
					Fee: feeConfiguratingInFront ? feeData.Fee : 0,
					DefaultFee: 0,
					Loading: false,
				})
			);
		}
	};

	const debouncedCalculateCommission = useRef(HelperService.debounce(calculateCommission, debouncedRequestTimeout));

	const inputRef = useRef(null);

	const convertedPrecision = TypesService.FiatPrecision;
	const quotes = useSelector(store => store.app.quotes);
	const currencyRate = quotes.find(quote => quote.Id === asset.Id)?.Rate || 0;
	const decimalConvertedAmount = new Decimal(amount).mul(currencyRate); //Decimal
	const convertedAmountString = decimalConvertedAmount.toString(); // string
	const formatToFloor = (value, precision) => HelperService.FormatCrypto(value, precision, 'floor');
	const amountInUSD = formatToFloor(convertedAmountString, convertedPrecision);

	const isRequiredInput =
		permissions?.paymentPurpose?.RequirePaymentPurpose &&
		amountInUSD >= permissions?.paymentPurpose?.MinPaymentPurposeAmountUsd;

	const prepareWithdrawal = async () => {
		if (!comment && !uploadFiles.length && isRequiredInput) {
			inputRef.current?.focus();
			return;
		}

		const commissionType =
			feeMethodSelecting || feeConfiguratingInFront ? commissionTypesByCurrencyId[feeData.CurrencyId] : 0;
		try {
			setLoading(true);
			const numberAmount = Number(isMaxAmountSetted ? account.Amount : amount);
			const sendAmount = feeConfiguratingInFront && !isMaxAmountSetted ? numberAmount + feeData.Fee : numberAmount;

			const response = await RequestService.WithdrawalPrepare(
				account.Address,
				recipient.Address || recipient,
				asset.Symbol,
				sendAmount,
				comment,
				commissionType,
				feeConfiguratingInFront ? feeData.Fee : null,
				uploadFiles.map(file => file.Id)
			);

			if (!response) return;
			if (response.Status !== 'Success') {
				throw Error();
			} else {
				dispatch(sendSlice.actions.setWithdrawalId(response.WithdrawalId));
				navigate(routes.sendConfirm, location);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
		}
	};

	const numericAmount = Number(amount);
	const isBalanceEnoughForAmount = numericAmount <= account?.Amount;
	const feeAmountForValidate = feeData.Loading && !feeData.FeeRedacted ? 0 : feeData.Fee;
	const needAmountCoverFee =
		asset.Id === feeData.CurrencyId &&
		!(isMaxAmountSetted && ((feeCalculateInBack && !currencyData.Standart) || feeConfiguratingInFront));
	const isBalanceEnoughForAmountAndFee = needAmountCoverFee
		? numericAmount + feeAmountForValidate <= account?.Amount
		: isBalanceEnoughForAmount;
	const isFeeAddressAmountEnoughForFee =
		feeAmountForValidate <= feeData.Amount || feeAmountForValidate <= feeData.GsBalance;
	const needwWaitForCalculateFee = !feeData.FeeRedacted && feeData.Loading;

	return (
		<div className='SendAmount'>
			{withdrawalCardId ? (
				<PageHeader header={t('Withdrawal')} collapsed />
			) : (
				<PageHeader
					header={<>{`${currencyData.Name}${currencyData.Standart && ' '}${currencyData.Standart}`}</>}
					icon={currencyData.CompanyIcon}
					iconLabel={currencyData.CompanyIcon !== currencyBlockchainData.Icon ? currencyBlockchainData.Icon : null}
					collapsed
				/>
			)}
			<StickyPanel
				controls={
					<Button
						className='SendAmount__continue'
						onClick={prepareWithdrawal}
						disabled={
							!numericAmount ||
							!isBalanceEnoughForAmountAndFee ||
							!isFeeAddressAmountEnoughForFee ||
							needwWaitForCalculateFee
						}
						loading={loading}
					>
						{t('Continue')}
					</Button>
				}
			>
				<div className='SendAmount__container'>
					{haveActiveTransactionWarning && (
						<div className='SendAmount__have-active-transaction'>
							<Icon type={Icons.EXCLAMATIONMARK_CIRCLE} width={WARNIN_ICON_SIZE} height={WARNIN_ICON_SIZE} />
							{t('Attention: The transaction is already in progress from this address.')}
						</div>
					)}
					<div className='SendAmount__body' ref={sendAmountBodyRef}>
						{recipient.Address ? (
							<AddressItem
								className='FormBlock SendAmount__recipient-box'
								address={{ ...recipient, Amount: null, AmountUsd: null }}
								lastItem
							/>
						) : (
							<div className='SendAmount__recipient FormBlock'>
								<div>{t('To')}</div>
								<div className='SendAmount__recipient-address'>{recipient}</div>
							</div>
						)}
						<SendAmountInput
							asset={asset}
							account={account}
							destinationAddress={recipient.Address || recipient}
							amount={amount}
							feeData={feeData}
							isMaxAmountSetted={isMaxAmountSetted}
							calculateCommission={debouncedCalculateCommission.current}
							setIsMaxAmountSetted={isAmountMax => dispatch(sendSlice.actions.setIsMaxAmountSetted(isAmountMax))}
							setAmountToStore={amountToStore => dispatch(sendSlice.actions.setAmount(amountToStore))}
							setFeeData={feeData => dispatch(sendSlice.actions.setFeeData(feeData))}
							minAmountUsd={minAmountUsd}
						/>
						{!withdrawalCardId && (
							// <Input
							// 	className='SendAmount__comment FormBlock'
							// 	placeholder={intl.formatMessage({ id: 'Comment' })}
							// 	value={comment}
							// 	onChange={updateComment}
							// 	maxLength={256}
							// />
							<PaymentPurpose
								comment={comment}
								setComment={updateComment}
								isRequired={isRequiredInput}
								externalRef={inputRef}
							/>
						)}
						<div className='SendPayment__fee-header'>{t('Network fee')}</div>
						<FeeItem
							feeData={{
								...feeData,
								Loading: feeData.Loading && !feeData.FeeRedacted,
							}}
						/>
						{feeMethodSelecting && (
							<FeePaymentMethod
								feeByCurrencyId={feeByCurrencyIdForUSDT}
								account={account}
								feeData={feeData}
								setFeeData={feeData => dispatch(sendSlice.actions.setFeeData(feeData))}
							/>
						)}
						<FeePaymentDisclaimer className={Margin.TopSmall} />
						{feeConfiguratingInFront && <FeePaymentConfiguration />}
					</div>
				</div>
			</StickyPanel>
		</div>
	);
};

export default SendAmount;
